import React, { useState, useEffect } from "react";
import { useNavigate, useLocation } from "react-router-dom";
import { motion } from "framer-motion";
import api from "../api";

import Sidebar from "./Sidebar";
import Header from "./Header";
import ShopFilterComponent from "./ShopFilterComponent";
import PageTitle from "./PageTitleCompoent";
import ProductCardComponent from "../components/ProductCardComponent";
import DeleteWithConfirmation from "../components/DeleteWithConfirmation";
import PawAnimation from "./PawAnimation";

import "../css/ViewProductsScreen.css";
// import VioletRibbon from "../assets/violet-ribbon.png";
// import PinkRibbon from "../assets/pink-ribbon.png";
// import YellowRibbon from "../assets/yellow-ribbon.png";

const containerVariants = {
  hidden: { opacity: 0 },
  visible: { opacity: 1, transition: { staggerChildren: 0.1 } },
};

const itemVariants = {
  hidden: { opacity: 0, y: 30 },
  visible: { opacity: 1, y: 0, transition: { type: "spring", stiffness: 200 } },
};

export default function ViewProductsScreen() {
  const navigate = useNavigate();
  const location = useLocation();
  const { shop } = location.state || {}; // ✅ From ShoppersAndShopScreen

  const [productList, setProductList] = useState([]);
  const [showFilters, setShowFilters] = useState(false);
  const [loading, setLoading] = useState(true);

  // --- Modal State ---
  const [isModalOpen, setIsModalOpen] = useState(false);
  const [productToDelete, setProductToDelete] = useState({
    id: null,
    name: "",
  });

  // --- Filter States ---
  const [filters, setFilters] = useState({ productName: false });
  const [inputValues, setInputValues] = useState({ productName: "" });

  // --- Fetch products for this shop ---
  useEffect(() => {
    if (!shop?.id) return;

    const fetchShopProducts = async () => {
      try {
        setLoading(true);
        const token = localStorage.getItem("accessToken");
        if (!token) return console.error("No access token found");

        const res = await api.get(`/admin/shops/${shop.id}/detail`, {
          headers: { Authorization: `Bearer ${token}` },
        });

        const orderProducts = (res.data.orders || []).map(
          (order) => order.product
        );
        const directProducts = res.data.products || [];

        // ✅ Merge & remove duplicates
        const allProducts = [...orderProducts, ...directProducts].reduce(
          (acc, p) => {
            if (p && !acc.some((x) => x.id === p.id)) acc.push(p);
            return acc;
          },
          []
        );

        // ✅ Updated: Include tags & discount ribbons
        const formatted = allProducts.map((p) => ({
          id: p.id,
          name: p.name,
          originalPrice: p.original_price,
          actualprice: p.actual_price,
          discount_percentage: p.discount_percentage || null,
          tag: p.tags || p.tag || null, // ✅ Added tag support
          image: p.image
            ? p.image.startsWith("http")
              ? p.image
              : `${api.defaults.baseURL.split("/api")[0]}${p.image}`
            : null,
        }));

        setProductList(formatted);
      } catch (err) {
        console.error("Failed to fetch products:", err);
      } finally {
        setLoading(false);
      }
    };

    fetchShopProducts();
  }, [shop?.id, location.key]);

  // --- Modal Handlers ---
  const handleOpenDeleteModal = (productId, productName) => {
    setProductToDelete({ id: productId, name: productName });
    setIsModalOpen(true);
  };
  const handleCloseDeleteModal = () => {
    setIsModalOpen(false);
    setProductToDelete({ id: null, name: "" });
  };
  const handleConfirmDelete = () => {
    if (productToDelete?.id) handleDelete(productToDelete.id);
    handleCloseDeleteModal();
  };

  // ✅ DELETE API call
  const handleDelete = async (productId) => {
    try {
      const token = localStorage.getItem("accessToken");
      if (!token) {
        console.error("No access token found");
        return;
      }

      await api.delete(`/products/${productId}/`, {
        headers: { Authorization: `Bearer ${token}` },
      });

      setProductList((prevList) =>
        prevList.filter((product) => product.id !== productId)
      );

      console.log(`Product ${productId} deleted successfully`);
    } catch (err) {
      console.error("Failed to delete product:", err);
    }
  };

  // --- Filter Handlers ---
  const handleInputChange = (key, value) =>
    setInputValues((prev) => ({ ...prev, [key]: value.trim().toLowerCase() }));
  const handleFilterChange = (key, value) =>
    setFilters((prev) => ({ ...prev, [key]: value }));

  const handleBack = () => navigate(-1);
  const handleEdit = (productId) =>
    navigate(`/edit-product/${productId}`, {
      state: { shopId: shop.id, productId },
    });

  // --- Add Product handler (✅ always send shopId properly) ---
  const handleAddProduct = () => {
    if (!shop?.id) {
      alert("Shop not found. Please go back and select a shop again.");
      return;
    }
    navigate("/add-product", { state: { shopId: shop.id } });
  };

  // --- Ribbon image logic (commented out as in original code) ---
  // const getRibbonImage = (product) => {
  //    if (product.tag === "Popular") return VioletRibbon;
  //    if (product.tag === "Steal Deals") return PinkRibbon;
  //    if (product.tag === "Great Deals") return PinkRibbon;
  //    if (product.discount_percentage) return YellowRibbon;
  //    return null;
  // };

  // --- Filtered products ---
  const filteredProducts = productList.filter((product) => {
    const matchName =
      !inputValues.productName ||
      product.name?.toLowerCase().includes(inputValues.productName);
    return matchName;
  });

  if (loading) {
    return (
      <div className="full-screen-loader">
        <PawAnimation />
      </div>
    );
  }

  return (
    <div
      className="view-products-container"
      initial={{ opacity: 0 }}
      animate={{ opacity: 1 }}
    >
      <Sidebar />
      <div className="view-products-main">
        <Header />
        <div className="view-products-content">
          <div
            className="view-products-heading-row"
            initial={{ y: -20, opacity: 0 }}
            animate={{ y: 0, opacity: 1 }}
          >
            <PageTitle
              title={`${shop?.shop_name || "Shop"}, ${
                shop?.address || "Location"
              }`}
              onBack={handleBack}
            />
            <div className="view-products">
              <ShopFilterComponent
                showFilters={showFilters}
                setShowFilters={setShowFilters}
                filters={filters}
                inputValues={inputValues}
                onFilterChange={handleFilterChange}
                onInputChange={handleInputChange}
                filterKeys={["productName"]}
              />
              <div className="shoppers-and-shop-filter-buttons">
                <motion.button
                  onClick={handleAddProduct}
                  className="shoppers-and-shop-button"
                  whileHover={{ scale: 1.05 }}
                  whileTap={{ scale: 0.9 }}
                >
                  <svg
                    xmlns="http://www.w3.org/2000/svg"
                    width={20}
                    height={20}
                    viewBox="0 0 24 24"
                  >
                    <path
                      fill="#eba8bf"
                      d="M19 12.998h-6v6h-2v-6H5v-2h6v-6h2v6h6z"
                    />
                  </svg>
                  <span className="shoppers-and-shop-button-text">
                    Add Product
                  </span>
                </motion.button>
              </div>
            </div>
          </div>

          <div
            className="view-products-grid"
            variants={containerVariants}
            initial="hidden"
            animate="visible"
          >
            {filteredProducts.length === 0 ? (
              <motion.p initial={{ opacity: 0 }} animate={{ opacity: 1 }}>
                No products found.
              </motion.p>
            ) : (
              filteredProducts.map((product) => (
                <div key={product.id} variants={itemVariants}>
                  <ProductCardComponent
                    shopId={shop?.id}
                    productId={product.id}
                    name={product.name}
                    image={product.image}
                    price={product.actualprice}
                    actualprice={product.originalPrice}
                    discount_percentage={product.discount_percentage} // ✅ Pass discount value
                    label={product.tag}
                    onEdit={() => handleEdit(product.id)}
                    onDelete={() =>
                      handleOpenDeleteModal(product.id, product.name)
                    }
                  />
                </div>
              ))
            )}
          </div>
        </div>
      </div>

      <DeleteWithConfirmation
        isOpen={isModalOpen}
        onCancel={handleCloseDeleteModal}
        onConfirm={handleConfirmDelete}
        title={`Delete "${productToDelete?.name}"?`}
        message="Are you sure you want to remove this product? This action cannot be undone."
      />
    </div>
  );
}
